import LocalStorage from '@/helpers/localStorage';
import { setActivePinia } from 'pinia';
import { pinia } from '@/stores';
import { useCheckoutStore } from '@/stores/useCheckoutStore';
import { cancelBooking } from '@/services/medkusApi/publicBookingsService';
import type { Cart, CartSale } from '@/types/cart';

function getCheckoutStore() {
  setActivePinia(pinia);
  return useCheckoutStore();
}

const STORAGE_KEY = 'crt';
const CART_TTL_MINUTES = 60 * 24; // keep prebook data for 24h

let expiryTimeoutId: ReturnType<typeof setTimeout> | null = null;

function resolveExpiryTimestamp(value: unknown): number | null {
  if (!value) {
    return null;
  }
  const parsed = value instanceof Date ? value : new Date(String(value));
  const timestamp = parsed.getTime();
  if (Number.isNaN(timestamp)) {
    return null;
  }
  return timestamp;
}

function isExpiredAt(value: unknown, now: number): boolean {
  const timestamp = resolveExpiryTimestamp(value);
  if (timestamp === null) {
    return false;
  }
  return timestamp <= now;
}

function collectNextExpiry(cart: Cart, now: number): number | null {
  let nextExpiry: number | null = null;
  cart.sales.forEach((sale) => {
    const timestamp = resolveExpiryTimestamp(sale.prebookExpiresAt);
    if (timestamp === null || timestamp <= now) {
      return;
    }
    if (nextExpiry === null || timestamp < nextExpiry) {
      nextExpiry = timestamp;
    }
  });
  const context = getCheckoutStore().checkoutContext as Record<string, unknown> | null;
  if (context && typeof context === 'object') {
    const sale = (context as { sale?: Record<string, unknown> | null }).sale;
    if (sale && typeof sale === 'object') {
      const contextExpiry = resolveExpiryTimestamp((sale as { prebookExpiresAt?: unknown }).prebookExpiresAt);
      if (contextExpiry !== null && contextExpiry > now && (nextExpiry === null || contextExpiry < nextExpiry)) {
        nextExpiry = contextExpiry;
      }
    }
  }
  return nextExpiry;
}

function scheduleExpiryCheck(cart: Cart): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (expiryTimeoutId !== null) {
    clearTimeout(expiryTimeoutId);
    expiryTimeoutId = null;
  }
  const now = Date.now();
  const nextExpiry = collectNextExpiry(cart, now);
  if (nextExpiry === null) {
    return;
  }
  const delay = Math.max(0, nextExpiry - now);
  expiryTimeoutId = window.setTimeout(() => {
    expiryTimeoutId = null;
    CartService.ensureCart();
  }, delay);
}

function purgeExpiredSales(cart: Cart, now: number): { cart: Cart; removed: CartSale[] } {
  if (!cart.sales.length) {
    return { cart, removed: [] };
  }
  const remaining: CartSale[] = [];
  const removed: CartSale[] = [];
  cart.sales.forEach((sale) => {
    if (isExpiredAt(sale.prebookExpiresAt, now)) {
      removed.push(sale);
    } else {
      remaining.push(sale);
    }
  });
  if (removed.length) {
    cart.sales = remaining;
  }
  return { cart, removed };
}

function keepLastSale(cart: Cart): boolean {
  if (cart.sales.length <= 1) {
    return false;
  }
  cart.sales = [cart.sales[cart.sales.length - 1]];
  return true;
}

function clearExpiredCheckoutContext(now: number): void {
  const context = getCheckoutStore().checkoutContext as Record<string, unknown> | null;
  if (!context || typeof context !== 'object') {
    return;
  }
  const sale = (context as { sale?: Record<string, unknown> | null }).sale;
  if (!sale || typeof sale !== 'object') {
    return;
  }
  if (isExpiredAt((sale as { prebookExpiresAt?: unknown }).prebookExpiresAt, now)) {
    getCheckoutStore().setCheckoutContext(null);
  }
}

function buildDefaultCart(): Cart {
  return {
    sales: [],
    sItems: [],
    total: 0,
    count: 0,
    paid: 0,
  };
}

function cloneCart(input: unknown): Cart {
  if (!input || typeof input !== 'object') {
    return buildDefaultCart();
  }

  const candidate = input as Partial<Cart>;
  return {
    sales: Array.isArray(candidate.sales) ? [...candidate.sales] : [],
    sItems: Array.isArray(candidate.sItems) ? [...candidate.sItems] : [],
    total: typeof candidate.total === 'number' ? candidate.total : 0,
    count: typeof candidate.count === 'number' ? candidate.count : 0,
    paid: typeof candidate.paid === 'number' ? candidate.paid : 0,
  };
}

function recalculateCart(cart: Cart): Cart {
  const pending = cart.sales.reduce((acc, sale) => acc + (Number(sale.pending) || 0), 0);
  const paid = cart.sales.reduce((acc, sale) => acc + (Number(sale.totalPaid) || 0), 0);
  cart.total = pending;
  cart.paid = paid;
  cart.count = cart.sales.length;
  return cart;
}

function persist(cart: Cart): Cart {
  const expires = new Date();
  expires.setMinutes(expires.getMinutes() + CART_TTL_MINUTES);
  LocalStorage.addWithTimout(STORAGE_KEY, cart, expires);
  getCheckoutStore().setCart(cart);
  scheduleExpiryCheck(cart);
  return cart;
}

async function cancelSaleRemote(sale: CartSale): Promise<void> {
  if (!sale?.bookingId || sale.cancelCustomerId === null || sale.cancelCustomerId === undefined) {
    return;
  }
  try {
    await cancelBooking(Number(sale.bookingId), Number(sale.cancelCustomerId), {
      tenantUrl: sale.tenantUrl ?? null,
    });
  } catch (error) {
    void error;
  }
}

export class CartService {
  static ensureCart(): Cart {
    const stateCart = getCheckoutStore().cart as Cart | null;
    const baseCart = stateCart && typeof stateCart === 'object'
      ? cloneCart(stateCart)
      : cloneCart(LocalStorage.getWithTimeout(STORAGE_KEY));
    recalculateCart(baseCart);
    const now = Date.now();
    const cleaned = purgeExpiredSales(baseCart, now);
    recalculateCart(cleaned.cart);
    const trimmed = keepLastSale(cleaned.cart);
    if (trimmed) {
      recalculateCart(cleaned.cart);
    }
    if (cleaned.removed.length || trimmed) {
      clearExpiredCheckoutContext(now);
      return persist(cleaned.cart);
    }
    getCheckoutStore().setCart(cleaned.cart);
    clearExpiredCheckoutContext(now);
    scheduleExpiryCheck(cleaned.cart);
    return cleaned.cart;
  }

  static getCart(): Cart {
    return this.ensureCart();
  }

  static addSale(sale: CartSale): Cart {
    if (!sale || typeof sale !== 'object' || sale.id === undefined || sale.id === null) {
      return this.ensureCart();
    }

    const cart = this.ensureCart();
    cart.sales = [{ ...sale }];
    recalculateCart(cart);
    return persist(cart);
  }

  static async removeSale(saleId: number | string, options: { cancelServer?: boolean } = {}): Promise<Cart> {
    const cart = this.ensureCart();
    const idAsString = saleId !== undefined && saleId !== null ? String(saleId) : null;
    if (!idAsString) {
      return cart;
    }

    const saleToRemove = cart.sales.find((entry) => String(entry.id) === idAsString);
    cart.sales = cart.sales.filter((entry) => String(entry.id) !== idAsString);

    recalculateCart(cart);
    const updated = persist(cart);

    if (options.cancelServer && saleToRemove) {
      await cancelSaleRemote(saleToRemove);
    }

    return updated;
  }

  static clear(): Cart {
    LocalStorage.delete(STORAGE_KEY);
    const cart = buildDefaultCart();
    getCheckoutStore().setCart(cart);
    scheduleExpiryCheck(cart);
    return cart;
  }
}

export default CartService;
